import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PublicTopNav from '../components/PublicTopNav';
import SiteFooter from '../components/SiteFooter';
import { COMPANY } from '../legal/company';
import { newsletterApi } from '../api/newsletter.api';

type State =
  | { step: 'working' }
  | { step: 'done'; email?: string }
  | { step: 'unknown' }
  | { step: 'failed' };

/**
 * Where the unsubscribe link in a newsletter lands.
 *
 * It acts on arrival rather than asking for a confirmation: clicking
 * "unsubscribe" in an email is already the decision, and a second step is the
 * kind of friction that gets a sender reported as spam instead. Nothing here
 * needs an account — somebody who left the list may well not have one.
 */
const UnsubscribePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<State>({ step: 'working' });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let active = true;

    if (!token) {
      setState({ step: 'unknown' });
      return;
    }

    newsletterApi
      .unsubscribe(token)
      .then((result) => {
        if (!active) return;
        setState(result.found ? { step: 'done', email: result.email } : { step: 'unknown' });
      })
      .catch(() => {
        if (active) setState({ step: 'failed' });
      });

    return () => {
      active = false;
    };
  }, [token]);

  return (
    <div className="min-h-screen bg-nova-bg flex flex-col">
      <PublicTopNav activePath="" />
      <main className="flex-1 px-6 lg:px-12 py-20">
        <div className="max-w-xl mx-auto nova-card">
          {state.step === 'working' && (
            <p role="status" className="text-nova-text2">
              Taking you off the list...
            </p>
          )}

          {state.step === 'done' && (
            <>
              <h1 className="font-display text-2xl font-extrabold text-nova-text mb-3">
                You have been unsubscribed
              </h1>
              <p className="text-nova-text2 leading-relaxed mb-2 break-words">
                {state.email
                  ? `${state.email} will no longer receive the ${COMPANY.brand} newsletter.`
                  : `That address will no longer receive the ${COMPANY.brand} newsletter.`}
              </p>
              <p className="text-sm text-nova-text3 leading-relaxed">
                You may still get messages you ask for, such as a sign-in code or a receipt for
                something you bought. Changed your mind? Subscribe again from our{' '}
                <Link to="/#newsletter" className="text-nova-sky hover:text-nova-sky2">
                  home page
                </Link>
                .
              </p>
            </>
          )}

          {state.step === 'unknown' && (
            <>
              <h1 className="font-display text-2xl font-extrabold text-nova-text mb-3">
                That link is not valid
              </h1>
              <p className="text-nova-text2 leading-relaxed">
                It may have been copied incompletely, or the address may already be off the list.
                Write to{' '}
                <a href={`mailto:${COMPANY.supportEmail}`} className="text-nova-sky hover:text-nova-sky2">
                  {COMPANY.supportEmail}
                </a>{' '}
                and we will remove it for you.
              </p>
            </>
          )}

          {state.step === 'failed' && (
            <>
              <h1 className="font-display text-2xl font-extrabold text-nova-text mb-3">
                Something went wrong
              </h1>
              <p className="text-nova-text2 leading-relaxed">
                We could not reach our server just now, so nothing has changed. Please reload this
                page, or write to{' '}
                <a href={`mailto:${COMPANY.supportEmail}`} className="text-nova-sky hover:text-nova-sky2">
                  {COMPANY.supportEmail}
                </a>{' '}
                and we will take the address off the list.
              </p>
            </>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default UnsubscribePage;
